import { RootState } from 'initializers/types';
import { createSelector } from 'reselect';

import { getCurrentUserIsManagerial } from 'selectors/session';

import { AvailabilityTemplate, Job } from 'features/availability/types';

import { formatDate } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { MY_AVAILABILITY_SLICE } from './constants';

export const selectUserAvailability = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).data;

export const selectUserAvailabilityLoading = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).loading;

export const selectSelectedTemplateId = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).selectedTemplateId;

export const selectAvailabilityTemplates = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).data?.availability_templates || [];

export const selectLocationOptions = createSelector(
  [selectUserAvailability],
  availabilityData => {
    const locationOptions = availabilityData?.jobs
      ? availabilityData.jobs.map((job: Job) => ({
          value: job.id,
          label: job.location_name,
        }))
      : [];

    return [
      {
        value: 'all',
        label: toI18n('my_availability_react.availability_modal.all_locations'),
      },
      ...locationOptions,
    ];
  }
);

export const selectSelectedTemplate = createSelector(
  [selectSelectedTemplateId, selectAvailabilityTemplates],
  (templateId, templates) => {
    if (!templateId) return null;

    return templates.find(
      (template: AvailabilityTemplate) => template.id === templateId
    );
  }
);

export const selectSelectedTemplateStatus = createSelector(
  [selectSelectedTemplate],
  selectedTemplate => selectedTemplate.status
);

export const selectTemplateSelectOptions = createSelector(
  [selectAvailabilityTemplates],
  templates => {
    // Sort templates by date (most recent first)
    // Current template should always be first
    const sortedTemplates = [...templates].sort((a, b) => {
      if (a.current) return -1;
      if (b.current) return 1;
      return b.start_at.localeCompare(a.start_at);
    });

    return sortedTemplates.map((template: AvailabilityTemplate) => ({
      value: template.id,
      label: template.current
        ? toI18n('my_availability_react.current')
        : formatDate(template.start_at, 'month_day_year_short'),
    }));
  }
);

export const selectIsSubmittingNotifyManager = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).isSubmittingNotifyManager;

export const selectIsSubmittingClearAvailability = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).isSubmittingClearAvailability;

export const selectIsSubmittingCreateTemplate = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).isSubmittingCreateTemplate;

export const selectAvailabilityError = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).availabilityError;

export const selectIsSubmittingNewAvailability = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).isSubmittingAvailability;

// This captures the approval_required field returned from the API
export const selectApprovalRequired = (state: RootState) =>
  state.get(MY_AVAILABILITY_SLICE).data?.approval_required || false;

/**
 * Determines if the current template needs approval based on two factors:
 * 1. Whether approval is required by the system (selectApprovalRequired)
 * 2. Whether the template is already in an approved state
 *
 * Unlike selectApprovalRequired which just returns the API setting,
 * this selector combines that setting with the template's current status
 * to determine if approval is actually needed at this moment.
 */
export const selectIsApprovalNeeded = createSelector(
  [selectSelectedTemplate, selectApprovalRequired],
  (selectedTemplate, approvalRequired) => {
    if (!selectedTemplate) return false;
    return approvalRequired && selectedTemplate.status !== 'approved';
  }
);

/**
 * Determines if a warning should be shown when making changes to availabilities based on:
 * 1. Template status (approved/rejected)
 * 2. User role (managerial/non-managerial)
 * 3. Approval requirements (approval_required key from API)
 */
export const selectShowAvailabilityWarning = createSelector(
  [
    selectSelectedTemplateStatus,
    getCurrentUserIsManagerial,
    selectApprovalRequired,
  ],
  (status, isManagerial, approvalRequired) => {
    /**
     * Rejected template behavior:
     * When approval is required: Show warning and reset status to unsubmitted when edits occur
     * When approval is NOT required: No warning needed, changes don't affect status
     *   (EDGE CASE THAT APPROVAL IS NOT REQUIRED AND STATUS IS REJECTED)
     * This matches the backbone flow where the Request Approval button remains disabled
     * for rejected templates, but users can still make changes
     */
    if (status === 'rejected') return approvalRequired;

    /**
     * If template is approved and user is non-managerial and approval is required,
     * show warning because changes will revert to pending status
     */
    if (status === 'approved' && !isManagerial && approvalRequired) return true;

    // Otherwise, no warning needed
    return false;
  }
);
